import {noop} from 'lodash'
import Q from 'q'
import {BehaviorSubject, Subject, Observable, combineLatest} from 'rxjs'
import {distinctUntilChanged, map, scan} from 'rxjs/operators'

import {
  validationResult,
  feedbackChange,
  submitValidationStarted,
  submitValidationEnded,
  submitStarted,
  submitEnded,
  reset,
} from './defaultFormDataReducer'
import {
  FORM_ROOT_FIELD_PATH,
  VALIDATION_ACTION_TYPE,
  VALIDATING_FLAG,
  INITIALIZED_STATE_FLAG,
  DATA_PRELOADING_FINISHED,
  SCROLL_TO_ACTION_TYPE,
} from './flags'
import type {
  FormDefinitionType,
  FormDefinitionMethodsType,
  ValidationResultType,
  PathType,
  FormDataType,
  ValidationStarterType,
  ChangeSpecType,
} from './Form'
import {
  waitForFlag,
  defaultSerializer,
  containsOnlyValidFields,
  findFirstInvalidField,
} from './util'

type PrefixedItemsInfo = {
  fieldsSerialized: Record<string, unknown>
  keys: ReadonlyArray<string>
}

export type FormAtPathType = {
  form: FormType
  path: string
  /**
   * stringified list of dependencies
   */
  dependencies?: string
  getPrefixedItemsInfo: () => PrefixedItemsInfo
  getPrefixedField: () => FormAtPathType
}

export type FormGetFieldType = (
  path: string,
  ...dependencies: ReadonlyArray<string>
) => FormAtPathType

export type ValidationActionType = {
  type: typeof VALIDATION_ACTION_TYPE
  paths?: ReadonlyArray<string>
}
export type ScrollToActionType = {
  type: typeof SCROLL_TO_ACTION_TYPE
  path?: string
}

/**
 * Delegated Bidirectional State Management
 */
export type FormMethodsType = {
  getField: FormGetFieldType
  dispatch(changeSpec: ChangeSpecType): void | Promise<void>
  /**
   * an Observable stream of actions performed on the Form (currently only validations)
   */
  actionStream: Observable<ValidationActionType | ScrollToActionType>
  /**
   * an Observable stream of changes caused by setData
   */
  changeStream: Observable<ChangeSpecType>
  /**
   * Dispatches an internal action to the actionStream
   * Each FieldController listens to that action
   * and starts its own validation in case it matches the path specified
   * or in case no paths are specified (global validation)
   */
  askToValidate(...paths: ReadonlyArray<PathType>): void
  /**
   * Use to schedule a validation that might be asynchronous on a given path
   */
  doValidate(path: PathType, validationStarter: ValidationStarterType): void
  /**
   * Performs SubmitValidation, if passed, returns onSubmit(serializedForm)
   * @return result of onSubmit(serializedForm) or 'undefined'
   */
  trySubmit(options?: any): Promise<any | void>

  // internal
  validateIfSubmittable(): Promise<boolean>
}

export type FormType = FormGetFieldType & FormMethodsType

export type CreatedFormType = {
  dispose: () => void
  /**
   * method used to update the internal data of the Form object
   */
  setData: (data: FormDataType) => void
  form: FormType
  setConfig: (validateWhenDataPreloaded: boolean) => void
}

export default function createForm(
  conf: FormDefinitionType & FormDefinitionMethodsType
): CreatedFormType {
  const {
    dispatch,
    data: initialData = {},
    onSubmit = noop,
    serializer = defaultSerializer,
    scrollToInvalidField,
    withNestedFields,
  } = conf

  const initialState = {
    ...initialData,
    [FORM_ROOT_FIELD_PATH]: {
      ...initialData[FORM_ROOT_FIELD_PATH],
      [INITIALIZED_STATE_FLAG]: true,
    },
  }
  const dataStream = new BehaviorSubject(initialState)
  const configStream = new BehaviorSubject(conf.validateWhenDataPreloaded)

  const changeStream = dataStream.pipe(
    distinctUntilChanged((prevData, data) => prevData === data),
    scan(
      ({data: prevData, changeCount}, data) => ({
        data,
        prevData,
        changeCount: changeCount + 1,
      }),
      {
        data: initialData,
        prevData: undefined as any,
        changeCount: 0,
      }
    )
  )

  const actionStream = new Subject()
  const dispatchAction = (action: any) => {
    // safe-guard if dispatchAction caller has been cancelled
    if (actionStream.closed) return
    actionStream.next(action)
  }
  const pendingValidations: Map<
    PathType,
    Promise<ValidationResultType>
  > = new Map()

  let getField

  // @ts-ignore
  const form: FormType = Object.assign(
    (getField = function field(
      path: string,
      ...dependencies: ReadonlyArray<string>
    ): FormAtPathType {
      return {
        form,
        path,
        dependencies: dependencies.join(', '),
        getPrefixedItemsInfo: () => {
          const fieldsInfo = Object.entries(dataStream.getValue()).reduce(
            (acc, [fieldKey, fieldValue]) => {
              const pathParts = fieldKey.split('.')
              const pathIndex = pathParts.indexOf(path)
              if (pathIndex !== -1) {
                acc.fieldsSerialized[fieldKey] = fieldValue
                acc.keys.push(fieldKey)
              }
              return acc
            },
            {
              fieldsSerialized: {},
              keys: [] as string[],
            }
          )
          return {
            fieldsSerialized: serializer(
              fieldsInfo.fieldsSerialized,
              false,
              false
            )[path],
            keys: fieldsInfo.keys,
          }
        },
        getPrefixedField: (...pathParts: ReadonlyArray<string>) =>
          getField(pathParts.join('.')),
      }
    }),
    {
      withNestedFields,
      actionStream: actionStream.asObservable(),
      changeStream,
      dispatch,
      getField,
      askToValidate(...paths: ReadonlyArray<PathType>) {
        dispatchAction(
          paths.length
            ? {type: VALIDATION_ACTION_TYPE, paths}
            : {type: VALIDATION_ACTION_TYPE}
        )
      },

      doValidate(path: PathType, validationStarter: ValidationStarterType) {
        const validationErrorOrPromise = validationStarter()
        if (Q.isPromiseAlike(validationErrorOrPromise)) {
          dispatch(
            feedbackChange({path, validationFeedback: {status: 'loading'}})
          )
          const pendingPromise = (
            validationErrorOrPromise as any as Promise<ValidationResultType>
          ).then((validationError) => {
            if (pendingValidations.get(path) === pendingPromise) {
              pendingValidations.delete(path)
              dispatch(
                validationResult({
                  path,
                  error: validationError,
                  showSuccess: true,
                })
              )
            }
            // ignore the promise if another validation was started later...
          })
          pendingValidations.set(path, pendingPromise)
        } else {
          // validationError is a value that we can set
          dispatch(
            validationResult({path, error: validationErrorOrPromise as any})
          )
        }
      },

      async validateIfSubmittable() {
        const validationsStarted = waitForFlag(
          changeStream,
          FORM_ROOT_FIELD_PATH,
          VALIDATING_FLAG,
          true
        )
        // ensure a consistent state:
        dispatch(submitValidationStarted())
        await validationsStarted
        // ask all fields to validate themselves:
        form.askToValidate()
        // await any async validations:
        await Promise.all(pendingValidations.values())
        const validationsReady = waitForFlag(
          changeStream,
          FORM_ROOT_FIELD_PATH,
          VALIDATING_FLAG,
          false
        )
        const formData = dataStream.getValue()
        const invalidField = findFirstInvalidField(formData)
        const invalidFieldPath = invalidField && invalidField[0]
        if (scrollToInvalidField && invalidFieldPath) {
          dispatchAction({
            type: SCROLL_TO_ACTION_TYPE,
            path: invalidFieldPath,
          })
        }
        // ensure a consistent, validated state after all validations have completed:
        dispatch(submitValidationEnded())
        await validationsReady

        return !invalidFieldPath
      },

      async trySubmit(options?: any) {
        dispatch(submitStarted())
        await form.validateIfSubmittable()
        const formData = dataStream.getValue()
        if (containsOnlyValidFields(formData)) {
          const submittingError = await onSubmit(
            serializer(formData),
            form,
            options
          )
          dispatch(submitEnded({error: submittingError}))
          return submittingError
        }
        dispatch(submitEnded({error: true}))
        return true // but passthrough an error
      },

      reset() {
        dispatch(reset({data: initialState}))
      },
    }
  )

  // NOTE: although this calls .subscribe(), this is never going to leak
  // since both parent Observables complete in dispose() below
  combineLatest(changeStream, configStream.pipe(distinctUntilChanged()))
    .pipe(
      map(
        ([{data}, shouldValidate]) =>
          shouldValidate &&
          data &&
          data[FORM_ROOT_FIELD_PATH] &&
          !!data[FORM_ROOT_FIELD_PATH][DATA_PRELOADING_FINISHED]
      ),
      distinctUntilChanged()
    )
    .subscribe(
      (validationTrigger) => validationTrigger && form.validateIfSubmittable()
    )

  return {
    dispose: () => {
      dataStream.complete()
      configStream.complete()
      actionStream.complete()
    },
    form,
    setData: (newData: FormDataType) => {
      dataStream.next(newData)
    },
    setConfig: (newConfig: any) => {
      configStream.next(newConfig)
    },
  }
}
